<template>
  <div class="row">
    <div v-for="(item, key) in timeSteps" :key="key + item.value" class="col-5">
      <DateTimePicker
        :id="key"
        class="date-time-input date-time-composed"
        :value="item.value"
        :required="item.required"
        :disabled="isTournamentFieldDisabled(key)"
        :date-label="$$t(item.label, `time-steps-${key}`)"
        hide-asterisk
        @input="handleDateChange($event, key)"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { translate } from '@/mixins/translate';
import dateMethods from '@/mixins/dateMethods';
import { ONE_HOUR, ONE_MINUTE } from '@/constants/time';
import { TOURNAMENT_STATUSES } from '@/constants/tournaments';
import DateTimePicker from '@/components/form/DateTimePicker';
const getTimeStepsConfig = () => ({
  tournament_start: {
    value: null,
    label: 'Tournament start:',
    required: true,
  },
  tournament_end: {
    value: null,
    label: 'End:',
    required: true,
  },
  registration_start: {
    value: null,
    label: 'Registration start:',
    required: true,
  },
  registration_end: {
    value: null,
    label: 'End:',
    required: true,
  },
});
export default {
  name: 'TournamentTimeFrameInput',
  inject: ['$validator'],
  components: {
    DateTimePicker,
  },
  mixins: [...dateMethods, translate('_web_tournament_settings')],
  props: {
    timeFrames: {
      required: true,
      type: Array,
    },
  },
  data() {
    return {
      timeSteps: this.$lodash.pick(getTimeStepsConfig(), this.timeFrames),
    };
  },
  computed: {
    ...mapGetters([
      'currentTournament',
      'isTournamentFieldDisabled',
      'currentTournamentSchedule',
      'isTournamentPastStatus',
    ]),
  },
  watch: {
    currentTournamentSchedule(newVal) {
      if (newVal && this.$route.params.tournamentID) {
        this.initializeDates();
      }
    },
  },
  created() {
    this.initializeDates();
  },
  methods: {
    ...mapMutations(['updateCurrentTournament']),
    initializeDates() {
      //change steps cloning to internal value
      const steps = this.$lodash.cloneDeep(this.timeSteps);
      const NOW = this.getNow();
      const roundToNearestHour = datetime =>
        datetime.get('minutes') > 29
          ? datetime.plus({ hours: 1 }).startOf('hour')
          : datetime.startOf('hour');
      const NEAREST_HOUR = roundToNearestHour(NOW.plus({ hours: 1 }));
      const IN_1_WEEK = roundToNearestHour(NOW.plus({ days: 7 }));
      for (let key in steps) {
        if (
          this.$route.params.tournamentID ||
          this.currentTournamentSchedule[key]
        ) {
          steps[key].value = this.currentTournamentSchedule[key];
        } else {
          steps[key].value = this.timeSteps[key].value;
        }
        //set default value
        if (
          key === 'registration_start' &&
          !this.currentTournamentSchedule[key]
        ) {
          steps[key].value = NEAREST_HOUR.toSeconds();
          this.handleDateChange(steps[key].value, 'registration_start');
        } else if (
          key === 'tournament_start' &&
          !this.currentTournamentSchedule[key]
        ) {
          steps[key].value = IN_1_WEEK.toLocal()
            .set({ hour: 18 })
            .toUTC()
            .toSeconds();
          this.handleDateChange(steps[key].value, 'tournament_start');
        }
      }
      // handle update of the props
      this.timeSteps = steps;
    },
    handleDateChange(seconds, prop) {
      if (seconds) {
        const pickedDate = this.secondsToDatetime(seconds);
        const limit = this.secondsToDatetime(this.calcMinDate(prop));
        const isSameDate =
          pickedDate.day === limit.day &&
          pickedDate.month === limit.month &&
          pickedDate.year === limit.year &&
          pickedDate.hour === limit.hour &&
          pickedDate.minute === limit.minute;
        const result =
          isSameDate && this.timeSteps[prop] && !this.timeSteps[prop].value
            ? limit.toSeconds()
            : seconds;
        if (this.timeSteps[prop]) {
          this.timeSteps[prop].value = result;
        }
        if (result) {
          this.updateCurrentTournament({
            [prop]: Math.floor(result),
            component: 'schedule',
          });
          this.errors.clear();
        }
      }
      if (
        prop === 'tournament_start' &&
        !this.isTournamentPastStatus(TOURNAMENT_STATUSES.REG_CLOSED) &&
        (!this.currentTournamentSchedule.registration_end ||
          this.currentTournamentSchedule.registration_end > seconds)
      ) {
        //set default registration end
        this.handleDateChange(seconds - ONE_HOUR, 'registration_end');
      }
    },
    calcMinDate(key) {
      const {
        check_in = {},
        tournament_start = {},
        registration_start = {},
        registration_end = {},
      } = this.timeSteps;

      const now = this.dateToSeconds(new Date());
      let limit =
        key === 'tournament_end'
          ? check_in.value ||
            tournament_start.value ||
            registration_end.value ||
            registration_start.value ||
            now
          : key === 'check_in'
          ? tournament_start.value ||
            registration_end.value ||
            registration_start.value ||
            now
          : key === 'tournament_start'
          ? now
          : key === 'registration_end'
          ? registration_start.value || now
          : now;
      limit = limit + ONE_MINUTE;
      return now > limit ? now : limit;
    },
    calcMaxDate(key) {
      const {
        registration_end = {},
        tournament_start = {},
        check_in = {},
        tournament_end = {},
      } = this.timeSteps;
      let limit =
        key === 'check_in'
          ? tournament_end.value || null
          : // : key === 'tournament_start'
          // ? check_in.value || tournament_end.value || null
          key === 'registration_end'
          ? tournament_start.value ||
            check_in.value ||
            tournament_end.value ||
            null
          : key === 'registration_start'
          ? registration_end.value ||
            tournament_start.value ||
            check_in.value ||
            tournament_end.value ||
            null
          : null;
      limit = limit ? limit - ONE_MINUTE : null;
      return limit;
    },
  },
};
</script>

<style lang="scss">
.date-time-input.date-time-composed {
  > div:not(:last-child) {
    @include auto-rtl(margin-right, 1rem);
  }
}
</style>
